import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { keys } from '@/api/hooks/shared/keys'
import {
  createStaffUser,
  getStaffRoleslist,
  getStaffUser,
  listStaffUsers,
  updateStaffUser,
  type CreateStaffUserBody,
  type UpdateStaffUserBody,
} from '@/api/map/staffUsers'

/** Catalog for the signed-in agent's access level (API also forces owner level). */
export function useStaffRoleslist(roleName: string | null | undefined) {
  const name = (roleName ?? '').trim()
  return useQuery({
    queryKey: [...keys.accessRoles, 'roleslists', name],
    enabled: Boolean(name),
    queryFn: () => getStaffRoleslist(name),
  })
}

export function useStaffUsers(query: { page?: number; keyword?: string }) {
  return useQuery({
    queryKey: keys.staffUsers(query),
    queryFn: () => listStaffUsers(query),
  })
}

export function useStaffUser(id: string | undefined) {
  return useQuery({
    queryKey: keys.staffUser(id ?? ''),
    enabled: Boolean(id),
    queryFn: () => getStaffUser(id!),
  })
}

export function useCreateStaffUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateStaffUserBody) => createStaffUser(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['staff-users'] })
    },
  })
}

export function useUpdateStaffUser(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateStaffUserBody) => updateStaffUser(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['staff-users'] })
      void qc.invalidateQueries({ queryKey: keys.staffUser(id) })
    },
  })
}
